import { ARENA } from '../../config/constants.js';

const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08;

/**
 * Mirror-Shade (Act II, ACT2-BIBLE.md §8) — an Antiphon echo: it carries a
 * tone, and only fire from a player attuned to that same tone can touch it.
 *
 * The boss version of polarity is about *taking* damage (matched bullets pass
 * through you). This is the mirror of it — polarity as a *dealing* problem.
 * A wave carries both tones, so clearing it means dashing to flip
 * (Player.attunement) rather than picking one and holding it, which is exactly
 * the habit the Antiphon fight will demand under real pressure.
 *
 * The rule is enforced in CombatSystem (where damage resolves and the player
 * is in scope), not here: `tone` is just a tag, and a mismatched shot passes
 * through for free rather than burning a piercing shot's hit charge.
 */
export class MirrorShade {
  constructor(eventBus, {
    x, y, radius = 5, hp, moveSpeed, contactDamage, tone = 'warm',
  }) {
    this.eventBus = eventBus;
    this.type = 'mirrorShade';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    // 'warm' | 'cold' — read by CombatSystem against player.attunement.
    this.tone = tone;
    this.isDead = false;
    this._contactCooldown = 0;
    this.hitFlashTimer = 0;
  }

  update(dt, player) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    this.x += (dx / dist) * this.moveSpeed * dt;
    this.y += (dy / dist) * this.moveSpeed * dt;
    this._clampToArena();

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: hpBefore - this.hp, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
